import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import { useContactMessagesQuery } from "../hooks/useContact";
import type { ContactMessage } from "../types";
import ContactDetailModal from "./ContactDetailModal";

const ContactPage = () => {
  const { t } = useTranslation("common");
  const { data, isLoading, isFetching } = useContactMessagesQuery();
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const messages = data?.data ?? [];
  const total = data?.total ?? 0;
  const isBusy = isLoading || isFetching;

  const handleRowClick = (message: ContactMessage) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "unread":
        return (
          <Badge className="bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30">
            {t("CONTACT.statusUnread")}
          </Badge>
        );
      case "read":
        return (
          <Badge className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30">
            {t("CONTACT.statusRead")}
          </Badge>
        );
      case "replied":
        return (
          <Badge className="bg-green-500/20 text-green-400 hover:bg-green-500/30">
            {t("CONTACT.statusReplied")}
          </Badge>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("CONTACT.title")} subtitle={t("CONTACT.subtitle")} />

      <Card className="border-neutral-800 bg-neutral-900/50">
        <Table>
          <TableHeader>
            <TableRow className="border-neutral-800 hover:bg-transparent">
              <TableHead className="text-neutral-400">{t("CONTACT.date")}</TableHead>
              <TableHead className="text-neutral-400">{t("CONTACT.name")}</TableHead>
              <TableHead className="text-neutral-400">{t("CONTACT.email")}</TableHead>
              <TableHead className="text-neutral-400">{t("CONTACT.subject")}</TableHead>
              <TableHead className="text-neutral-400">{t("CONTACT.status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {messages.map((message) => (
              <TableRow
                key={message.id}
                className="border-neutral-800 hover:bg-neutral-800/50 cursor-pointer"
                onClick={() => handleRowClick(message)}
              >
                <TableCell className="text-sm text-neutral-300">
                  {formatDistanceToNow(new Date(message.createdAt), {
                    addSuffix: true,
                    locale: es,
                  })}
                </TableCell>
                <TableCell className="font-medium text-neutral-100">{message.name}</TableCell>
                <TableCell className="text-neutral-400">{message.email}</TableCell>
                <TableCell className="text-neutral-200 max-w-[300px] truncate">
                  {message.subject}
                </TableCell>
                <TableCell>{getStatusBadge(message.status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {isBusy && <LoadingOverlay />}

        {!isLoading && messages.length === 0 && (
          <EmptyState
            title={t("CONTACT.emptyTitle")}
            description={t("CONTACT.emptyDescription")}
            className="m-6"
          />
        )}

        <div className="border-t border-neutral-800 px-4 py-3 text-xs text-neutral-400">
          Total: {total} {t("CONTACT.messagesLabel", { count: total })}
        </div>
      </Card>

      <ContactDetailModal
        message={selectedMessage}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
      />
    </div>
  );
};

export default ContactPage;
